<script lang="ts">
  import { createEventDispatcher, onMount, tick } from "svelte";
  import sendIcon from "./assets/images/send.icon.svg";
  import { sendMessage, sendQr } from "./service/service";
  import {
    apiTokenValue,
    messageList,
    sendMessageLoading,
    botInfo,
    fetchMessageLoading,
  } from "./store";
  import type { QuickReply } from "./service/service";
  import SkeletonAvatar from "./SharedComponent/SkeletonAvatar.svelte";
  export let expanded: boolean;

  let formData = {
    message: "",
  };

  let inputElement: HTMLTextAreaElement;
  const dispatch = createEventDispatcher();

  onMount(() => {
    inputElement.focus();
  });

  export async function handleQuickReply(qr: QuickReply) {
    const qrId = qr.id;
    const message = qr.question;

    messageList.update((messages) => [...messages, { content: message, role: "user" }]);
    dispatch("messageReceived", true);
    formData.message = "";
    await tick();
    inputElement.focus();
    sendMessageLoading.set(true);
    try {
      const sentMessage = await sendQr(qrId, $apiTokenValue);
      messageList.update((messages) => [...messages, sentMessage]);
      dispatch("messageReceived", true);
    } catch (error) {
      console.error("Failed to send message:", error);
    } finally {
      sendMessageLoading.set(false);
      inputElement.focus();
    }
  }

  async function handleSubmit(event: any) {
    event.preventDefault();
    if ($sendMessageLoading || !formData.message || formData.message.length === 0) {
      return;
    }
    const message = { ...formData }.message;

    messageList.update((messages) => [...messages, { content: message, role: "user" }]);
    dispatch("messageReceived", true);
    formData.message = "";
    await tick();
    inputElement.focus();
    sendMessageLoading.set(true);
    try {
      const sentMessage = await sendMessage(message, $apiTokenValue);
      messageList.update((messages) => [...messages, sentMessage]);
      dispatch("messageReceived", true);
    } catch (error) {
      console.error("Failed to send message:", error);
    } finally {
      sendMessageLoading.set(false);
      inputElement.focus();
    }
  }

  function handleKeyDown(event: KeyboardEvent) {
    if (event.key === "Enter") {
      if (event.shiftKey) {
        return;
      } else {
        event.preventDefault();
        handleSubmit(event);
      }
    }
  }
  export const directionDetector = (message?: string): "rtl" | "ltr" => {
    if (!message || message.trim().length === 0) {
      return "ltr";
    }
    const firstChar = message.slice(0, 1);

    const arabicChars = (firstChar.match(/[\u0600-\u06FF]/g) || []).length;
    if (arabicChars) {
      return "rtl";
    }

    return "ltr";
  };
</script>

<form
  class="concierge-chat-box-input"
  class:concierge-chat-box-input-expanded={expanded}
  on:submit={handleSubmit}
>
  <textarea
    id="message"
    placeholder="Ask your question..."
    bind:value={formData.message}
    bind:this={inputElement}
    required
    on:keydown={handleKeyDown}
    style="direction: {directionDetector(formData.message)};"
  />

  {#if $fetchMessageLoading}
    <span class="concierge-dummy-submit">
      <SkeletonAvatar />
    </span>
  {:else}
    <button
      type="submit"
      disabled={$sendMessageLoading || formData.message?.trim().length === 0}
      style="--element-background-color: {$botInfo?.settings?.widgetAppearanceBehavior
        ?.widgetBackgroundColor || '#0e3c26'};"
    >
      <img src={sendIcon} alt="send" />
    </button>
  {/if}
</form>

<style>
  .concierge-chat-box-input {
    height: 55px;
    background-color: rgb(255, 255, 255);
    border-radius: 0px 0px 10px 10px;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;
    padding: 0px 10px;
    gap: 10px;
    margin-top: 10px;
  }
  .concierge-chat-box-input > textarea {
    margin: 0px;
    flex: 1;
    line-height: 20px;
    height: 30px;
    border: none;
    outline: none;
    border-radius: 10px;
    padding: 0px 5px;
    font-size: 12px;
    outline: none;
    font-weight: normal;
    padding: 0px 10px;
    box-shadow: 0 0 0 2px rgba(135, 5, 255, 0.1);
    background-color: #f5f5f5;
    resize: none;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial,
      "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol",
      "Noto Color Emoji";
  }

  .concierge-chat-box-input-expanded {
    height: 75px;
  }
  .concierge-chat-box-input-expanded > textarea {
    height: 50px;
  }
  .concierge-chat-box-input > textarea::placeholder {
    color: #d2d2d2;
    line-height: inherit;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial,
      "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol",
      "Noto Color Emoji";
  }
  .concierge-chat-box-input > button {
    border: none;
    outline: none;
    height: 30px !important;
    width: 30px !important;
    border-radius: 50%;
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: var(--element-background-color);
    padding: 0px;
  }
  .concierge-chat-box-input > button:disabled {
    background-color: #d2d2d2;
    cursor: not-allowed;
  }
  .concierge-chat-box-input > button > img {
    width: 20px;
  }
  .concierge-dummy-submit {
    height: 30px;
    width: 30px;
    border-radius: 50%;
  }
</style>
